import React from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Empty,
  Divider,
  Table,
  Tag,
  Progress,
  Button,
  Spin,
} from 'antd';
import {
  AppstoreAddOutlined,
  DollarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  AggregatedCycleData,
  renderTotalCol,
  formatDateTime,
} from './cycleDetailHelpers';

export interface CycleDetailOutputsTabProps {
  aggregatedData: AggregatedCycleData;
  unitOfMeasure?: string;
  isLoadingLogs: boolean;
  isMobile?: boolean;
  filterBar: React.ReactNode;
  onSelectLog: (log: any) => void;
}

export const CycleDetailOutputsTab: React.FC<CycleDetailOutputsTabProps> = ({
  aggregatedData,
  unitOfMeasure = 'unidades',
  isLoadingLogs,
  isMobile = false,
  filterBar,
  onSelectLog,
}) => {
  const {
    totalOutputQuantity,
    totalOutputValue,
    balanceEstimado,
    outputsSummaryData,
    allOutputs,
  } = aggregatedData;

  return (
    <Card bodyStyle={{ padding: isMobile ? '12px 8px' : '20px' }}>
      {filterBar}
      {isLoadingLogs ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* KPIs de Producción */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card
                size="small"
                style={{
                  background: '#f6ffed',
                  borderColor: '#b7eb8f',
                  borderRadius: 8,
                }}
              >
                <Statistic
                  title={
                    <span style={{ color: '#389e0d', fontWeight: 600 }}>
                      Producción Total Cosechada
                    </span>
                  }
                  value={totalOutputQuantity}
                  prefix={<AppstoreAddOutlined style={{ color: '#389e0d' }} />}
                  suffix={
                    <span style={{ fontSize: 13, color: '#888' }}>
                      {unitOfMeasure}
                    </span>
                  }
                  valueStyle={{ color: '#389e0d', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card
                size="small"
                style={{
                  background: '#e6f7ff',
                  borderColor: '#91d5ff',
                  borderRadius: 8,
                }}
              >
                <Statistic
                  title={
                    <span style={{ color: '#096dd9', fontWeight: 600 }}>
                      Valor Estimado de Producción
                    </span>
                  }
                  value={totalOutputValue}
                  prefix={<DollarOutlined style={{ color: '#096dd9' }} />}
                  formatter={(val) =>
                    `$ ${new Intl.NumberFormat('es-CO').format(Number(val))}`
                  }
                  valueStyle={{ color: '#096dd9', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card
                size="small"
                style={{
                  background: balanceEstimado >= 0 ? '#f6ffed' : '#fff1f0',
                  borderColor: balanceEstimado >= 0 ? '#b7eb8f' : '#ffa39e',
                  borderRadius: 8,
                }}
              >
                <Statistic
                  title={
                    <span
                      style={{
                        color: balanceEstimado >= 0 ? '#389e0d' : '#cf1322',
                        fontWeight: 600,
                      }}
                    >
                      Balance del Periodo (Est.)
                    </span>
                  }
                  value={balanceEstimado}
                  prefix={
                    <DollarOutlined
                      style={{
                        color: balanceEstimado >= 0 ? '#389e0d' : '#cf1322',
                      }}
                    />
                  }
                  formatter={(val) =>
                    `$ ${new Intl.NumberFormat('es-CO').format(Number(val))}`
                  }
                  valueStyle={{
                    color: balanceEstimado >= 0 ? '#389e0d' : '#cf1322',
                    fontWeight: 'bold',
                  }}
                />
              </Card>
            </Col>
          </Row>

          {allOutputs.length === 0 ? (
            <Empty description="No se registraron cosechas o salidas de producción en el periodo seleccionado." />
          ) : (
            <>
              {/* Tabla Resumen Agrupado por Producto */}
              <Divider
                orientation="left"
                style={{ fontWeight: 600, color: '#262626' }}
              >
                📊 Resumen Acumulado por Producto Producido
              </Divider>
              <Table
                size="small"
                pagination={false}
                dataSource={outputsSummaryData}
                rowKey="id"
                scroll={{ x: 700 }}
                style={{ marginBottom: 28 }}
                columns={[
                  {
                    title: 'Producto Obtenido',
                    dataIndex: 'name',
                    key: 'name',
                    width: 190,
                    render: (val) => <strong>{val}</strong>,
                  },
                  {
                    title: 'Cant. Total Obtenida',
                    dataIndex: 'totalQuantity',
                    key: 'totalQuantity',
                    width: 130,
                    align: 'center',
                    render: (val) => (
                      <Tag color="green">
                        {val} {unitOfMeasure}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Cosechas / Salidas',
                    dataIndex: 'count',
                    key: 'count',
                    width: 120,
                    align: 'center',
                    render: (val) =>
                      `${val} registro${val > 1 ? 's' : ''}`,
                  },
                  {
                    title: 'Valor Estimado Total',
                    dataIndex: 'totalValue',
                    key: 'totalValue',
                    width: 140,
                    render: (val) => (
                      <span style={{ fontWeight: 600, color: '#52c41a' }}>
                        $ {new Intl.NumberFormat('es-CO').format(val)}
                      </span>
                    ),
                  },
                  {
                    title: '% de la Producción',
                    key: 'pct',
                    width: 120,
                    render: (_, record) => {
                      const pct =
                        totalOutputValue > 0
                          ? (record.totalValue / totalOutputValue) * 100
                          : 0;
                      return (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 100,
                          }}
                        >
                          <Progress
                            percent={Math.round(pct)}
                            size="small"
                            strokeColor="#52c41a"
                            style={{ flex: 1 }}
                          />
                        </div>
                      );
                    },
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row
                    style={{ background: '#fafafa', fontWeight: 'bold' }}
                  >
                    <Table.Summary.Cell index={0}>
                      Total General
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      <Tag color="green">
                        {outputsSummaryData.reduce(
                          (s, o) => s + o.totalQuantity,
                          0
                        )}{' '}
                        {unitOfMeasure}
                      </Tag>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="center">
                      {allOutputs.length} registros
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <span style={{ color: '#52c41a' }}>
                        $ {new Intl.NumberFormat('es-CO').format(totalOutputValue)}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>100%</Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />

              {/* Detalle Cronológico de Producción */}
              <Divider
                orientation="left"
                style={{ fontWeight: 600, color: '#262626' }}
              >
                📋 Detalle Cronológico de Cosechas y Producción
              </Divider>
              <Table
                size="small"
                dataSource={allOutputs}
                rowKey={(record) => `${record.id}-${record.log_date}`}
                scroll={{ x: 800 }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                }}
                columns={[
                  {
                    title: 'Fecha',
                    dataIndex: 'log_date',
                    key: 'log_date',
                    render: (val) => formatDateTime(val),
                    width: 170,
                  },
                  {
                    title: 'Etapa',
                    dataIndex: 'stage_name',
                    key: 'stage',
                    width: 110,
                    render: (val) => (val ? <Tag>{val}</Tag> : '-'),
                  },
                  {
                    title: 'Producto Producido',
                    key: 'element_name',
                    width: 170,
                    render: (_, record) => (
                      <b>{record.storage_elements?.name || 'Producto'}</b>
                    ),
                  },
                  {
                    title: 'Cant.',
                    dataIndex: 'quantity',
                    key: 'qty',
                    width: 80,
                    align: 'center',
                  },
                  {
                    title: 'Valor Unit. Est.',
                    dataIndex: 'unit_price',
                    key: 'price',
                    width: 110,
                    render: (val) =>
                      val ? `$${new Intl.NumberFormat('es-CO').format(val)}` : '-',
                  },
                  {
                    title: 'Valor Total Est.',
                    key: 'total',
                    width: 120,
                    render: renderTotalCol,
                  },
                  {
                    title: 'Detalle',
                    key: 'action',
                    width: 90,
                    align: 'center',
                    render: (_, record) => (
                      <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        onClick={() => onSelectLog(record.parent_log)}
                      >
                        Ver Log
                      </Button>
                    ),
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </Card>
  );
};
